import SaveBar from '@/components/forms/SaveBar'
import { AppUpdateGalleryInput } from '@/components/app-updates/AppUpdateGalleryInput'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useIsReadOnly } from '@/lib/casl'
import { MediaUploadUsage } from '@/lib/enums/MediaUploadUsage'
import { useAppForm } from '@/lib/form'
import { PhotoManagerMode } from '@/lib/photo-manager-config'
import {
  getAppUpdateFormDefaults,
  appUpdateAudienceOptions,
  appUpdateFormSchema,
  appUpdateTypeOptions,
  toAppUpdatePayload,
} from '@/lib/services/app-updates/app-updates.schema'
import {
  useCreateAppUpdateMutation,
  useUpdateAppUpdateMutation,
} from '@/lib/services/app-updates/app-updates.query'
import { getErrorMessage } from '@/lib/utils'
import { useNavigate } from '@tanstack/react-router'
import { Plus, X } from 'lucide-react'
import { toast } from 'sonner'

const MIN_BENEFIT_INPUTS = 3
const MAX_BENEFIT_INPUTS = 5

function getUploadedMedia(response) {
  const media = response?.body?.data ?? response?.body ?? response?.data ?? response

  return media?.id && media?.url ? media : null
}

function getBenefitRows(value) {
  if (Array.isArray(value) && value.length > 0) {
    return value.slice(0, MAX_BENEFIT_INPUTS)
  }

  return Array.from({ length: MIN_BENEFIT_INPUTS }, () => '')
}

function BenefitInputs({ field }) {
  const rows = getBenefitRows(field.state.value)

  const updateRows = (nextRows) => {
    field.handleChange(nextRows.slice(0, MAX_BENEFIT_INPUTS))
  }

  const updateRow = (index, value) => {
    const nextRows = [...rows]
    nextRows[index] = value
    updateRows(nextRows)
  }

  const addRow = () => {
    if (rows.length >= MAX_BENEFIT_INPUTS) {
      return
    }

    updateRows([...rows, ''])
  }

  const removeRow = (index) => {
    const nextRows = rows.filter((_, rowIndex) => rowIndex !== index)
    updateRows(nextRows.length > 0 ? nextRows : [''])
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label>Benefits</Label>
        {rows.length < MAX_BENEFIT_INPUTS ? (
          <Button type="button" variant="outline" size="sm" onClick={addRow}>
            <Plus className="size-4" />
            Add benefit
          </Button>
        ) : null}
      </div>

      <div className="space-y-2">
        {rows.map((benefit, index) => (
          <div key={index} className="flex items-center gap-2">
            <Input
              aria-label={`Benefit ${index + 1}`}
              placeholder={
                index === 0
                  ? 'Save time on daily order review'
                  : index === 1
                    ? 'Reduce missed follow-ups'
                    : 'Make the next step easier'
              }
              value={benefit ?? ''}
              maxLength={240}
              onChange={(event) => updateRow(index, event.target.value)}
              onBlur={field.handleBlur}
            />
            {rows.length > 1 ? (
              <Button
                type="button"
                variant="outline"
                size="icon"
                aria-label={`Remove benefit ${index + 1}`}
                onClick={() => removeRow(index)}
              >
                <X className="size-4" />
              </Button>
            ) : null}
          </div>
        ))}
      </div>

      <Typography variant="supporting" className="block">
        Add up to five short benefits. Blank rows are ignored when the app update is saved.
      </Typography>
    </div>
  )
}

export function AppUpdateForm({ mode, appUpdate = null, onSaved = null }) {
  const isCreate = mode === 'create'
  const createMutation = useCreateAppUpdateMutation()
  const updateMutation = useUpdateAppUpdateMutation()
  const mutation = isCreate ? createMutation : updateMutation
  const navigate = useNavigate()
  const path = usePathHelper()
  const { isReadOnly } = useIsReadOnly()

  const form = useAppForm({
    defaultValues: getAppUpdateFormDefaults(appUpdate),
    validators: {
      onSubmit: appUpdateFormSchema,
      onSubmitAsync: async ({ value }) => {
        if (isReadOnly) {
          return
        }

        const payload = toAppUpdatePayload(value)

        try {
          if (isCreate) {
            const response = await createMutation.mutateAsync(payload)
            const savedAppUpdate = response?.data ?? response

            toast.success('App update created')

            if (onSaved) {
              await onSaved(savedAppUpdate)
              return
            }

            if (savedAppUpdate?.id) {
              await navigate({
                ...path.whatsNew.show(savedAppUpdate.id).link,
                ignoreBlocker: true,
              })
            } else {
              await navigate({ ...path.whatsNew.index().link, ignoreBlocker: true })
            }

            return
          }

          const response = await updateMutation.mutateAsync({
            appUpdateId: appUpdate.id,
            values: payload,
          })
          const savedAppUpdate = response?.data ?? response

          toast.success('App update saved')
          await onSaved?.(savedAppUpdate)
        } catch (error) {
          toast.error(isCreate ? 'Failed to create app update' : 'Failed to save app update', {
            description: getErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  return (
    <form.AppForm>
      <form.Form className="grid gap-5 xl:grid-cols-[minmax(0,1.4fr)_minmax(20rem,0.6fr)] xl:items-start">
        <div className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle>Update Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-4">
                <form.AppField name="title">
                  {(field) => (
                    <field.TextInput
                      label="Title"
                      placeholder="Export gallery photos"
                      characterMaxLength={160}
                    />
                  )}
                </form.AppField>
              </div>

              <form.AppField name="description_html">
                {(field) => (
                  <field.RichTextInput
                    label="Description"
                    plainTextFieldName="description"
                    placeholder="Write the short update copy florists should read..."
                  />
                )}
              </form.AppField>

              <form.AppField name="benefits">
                {(field) => <BenefitInputs field={field} />}
              </form.AppField>

              <form.AppField name="scribe_url">
                {(field) => (
                  <field.TextareaInput
                    label="Scribe Embed Code"
                    placeholder='<iframe src="https://scribehow.com/embed/..." width="100%" height="800" allow="fullscreen"></iframe>'
                    rows={4}
                  />
                )}
              </form.AppField>

              <form.AppField name="destination_url">
                {(field) => (
                  <field.TextInput
                    label="Destination URL"
                    placeholder="/{orgId}/settings/checkout"
                    inputMode="url"
                  />
                )}
              </form.AppField>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Optional Content</CardTitle>
            </CardHeader>
            <CardContent>
              <form.AppField name="gallery">{() => <AppUpdateGalleryInput />}</form.AppField>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardHeader>
              <CardTitle>Publishing</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-1">
                <form.AppField name="audience">
                  {(field) => (
                    <field.RadioButtonGroup
                      label="Audience"
                      options={appUpdateAudienceOptions}
                      classNames={{
                        container: 'w-full',
                        button: 'flex-1',
                      }}
                    />
                  )}
                </form.AppField>
                <form.AppField name="update_type">
                  {(field) => (
                    <field.SelectInput label="Update Type" options={appUpdateTypeOptions} />
                  )}
                </form.AppField>
              </div>

              <form.AppField name="update_date">
                {(field) => (
                  <field.DatePicker
                    label="Update Date"
                    format="MMM D, YYYY"
                    placeholder="Today"
                    className="w-full justify-between"
                  />
                )}
              </form.AppField>

              <form.AppField name="is_published">
                {(field) => (
                  <field.SwitchInput
                    label="Published"
                    description="Organization users can see this update once it is published."
                  />
                )}
              </form.AppField>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Thumbnail</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Typography variant="supporting" className="block">
                Optional. When no thumbnail is selected, What's New uses a consistent fallback based
                on the update type.
              </Typography>
              <form.AppField name="thumbnail">
                {(field) => (
                  <field.MediaUploadInput
                    uploadUsage={MediaUploadUsage.ImageStandard.Value}
                    photoManagerMode={PhotoManagerMode.UploadOnly}
                    onUploadSuccess={(_, response) => {
                      const media = getUploadedMedia(response)

                      if (media) {
                        field.setValue(media)
                        return
                      }

                      toast.error(
                        'Thumbnail uploaded, but the response could not be added to this app update'
                      )
                    }}
                    onUploadError={(_, error) => {
                      toast.error('Failed to upload thumbnail', {
                        description: getErrorMessage(error),
                      })
                    }}
                    placeholderClassName="aspect-video w-full max-w-full rounded-lg"
                  />
                )}
              </form.AppField>
            </CardContent>
          </Card>
        </div>

        <SaveBar
          mutation={mutation}
          submitButtonText={isCreate ? 'Create' : 'Save'}
          resetButtonText="Reset"
        />
      </form.Form>
    </form.AppForm>
  )
}
